import React, { useCallback, useState, useMemo } from "react";

import { CopyIcon as Copy, MoreIcon, SuccessIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import * as Dropdown from "ds/Dropdown";
import getUrl from "Libs/getUrl";
import { copyToClipboard, getSubscriptionEditUrl } from "Libs/utils";
import {
  selectFeatureEditPlanEnabled,
  selectFeatureUsageBasedBillingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { useAppSelector } from "Store/hooks";

import * as S from "./OrganizationProjectMoreLinks.styles";

import type { OrganizationProject } from "Api/organizations/projects/OrganizationProjects.types";

type Props = {
  projectId: string;
  projectSubscriptionId: string;
  organizationName: string;
  project: OrganizationProject;
  hasProjectAccess: boolean;
};

export const OrganizationProjectMoreLinks = ({
  projectId,
  projectSubscriptionId,
  organizationName,
  project,
  hasProjectAccess
}: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const editPlanEnabled = useAppSelector(selectFeatureEditPlanEnabled);
  const usageBasedBillingEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );

  const subscriptionEditUrl = useMemo(
    () => getSubscriptionEditUrl({ project }),
    [project]
  );

  const intl = useIntl();

  const showEditPlan = !!(editPlanEnabled && subscriptionEditUrl?.url);

  const copyId = useCallback(
    async e => {
      // The parent is a link, so we need to prevent the default behavior
      e.preventDefault();
      e.stopPropagation();
      await copyToClipboard(projectId);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 500);
    },
    [projectId]
  );
  return (
    <Dropdown.Root id="project_links">
      <Dropdown.Trigger
        aria-label="dropdown-project"
        onClick={e => {
          // The parent is a link, so we need to prevent the default behavior
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <MoreIcon />
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <S.ContainerContent align="end">
          <S.HeaderText weight="semiBold" size="extraSmall" $color="granite">
            {intl.formatMessage({
              id: "projects.toggle.sort_by.project_id",
              defaultMessage: "Project ID"
            })}
          </S.HeaderText>

          <S.ListButton
            analyticId="project.morelinks.id"
            variant="secondary"
            onClick={copyId}
            className="copy-project-id"
          >
            {projectId || projectSubscriptionId}

            <S.Icon>
              {isCopied ? (
                <SuccessIcon aria-hidden="true" />
              ) : (
                <Copy aria-hidden="true" />
              )}
            </S.Icon>
          </S.ListButton>

          {(hasProjectAccess || showEditPlan) && <S.MenuSeparator />}

          {hasProjectAccess && (
            <>
              <S.HeaderText
                weight="semiBold"
                size="extraSmall"
                $color="granite"
              >
                {intl.formatMessage({
                  id: "project.state.not_live",
                  defaultMessage: "Go live"
                })}
              </S.HeaderText>

              <S.ListItem
                analyticId="project.morelinks.add_domain"
                variant="secondary"
                to={getUrl({
                  key: "organization.project.settings.domains",
                  props: {
                    organizationId: organizationName || "",
                    projectId: projectId || ""
                  }
                })}
              >
                {intl.formatMessage({
                  id: "settings.domains.add.button",
                  defaultMessage: "Add domain"
                })}
              </S.ListItem>

              <S.MenuSeparator />

              <S.ListItem
                analyticId="project.morelinks.project_access"
                variant="secondary"
                to={getUrl({
                  key: "organization.project.settings.access",
                  props: {
                    organizationId: organizationName || "",
                    projectId: projectId || ""
                  }
                })}
              >
                {intl.formatMessage({
                  id: "project_access",
                  defaultMessage: "Project access"
                })}
              </S.ListItem>

              <S.ListItem
                analyticId="project.morelinks.project_settings"
                variant="secondary"
                to={`/${organizationName}/${projectId}/-/settings`}
              >
                {intl.formatMessage({
                  id: "project_setting",
                  defaultMessage: "Project settings"
                })}
              </S.ListItem>
            </>
          )}

          {showEditPlan && (
            <S.ListItem
              analyticId="project.morelinks.edit"
              variant="secondary"
              to={
                usageBasedBillingEnabled
                  ? getUrl({
                      key: "organization.project.usageBasedBilling",
                      props: {
                        organizationId: organizationName,
                        projectId,
                        subscriptionId: projectSubscriptionId
                      }
                    })
                  : subscriptionEditUrl.url
              }
            >
              {intl.formatMessage({
                id: usageBasedBillingEnabled
                  ? "organization.billing.breakdown.project_billing"
                  : "project.morelinks.edit"
              })}
            </S.ListItem>
          )}
        </S.ContainerContent>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
